import { RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PanelSkeleton } from '@/components/ui/skeleton';
import { useSalesOrderStore } from '../store/salesOrderStore';

// Bridges the gap between clicking an FO and its detail arriving. Renders an
// instant header from the list-row summary plus a skeleton while getById runs
// (so the click never looks like a freeze), or a compact retry if the load
// failed. Returns null once detail is loaded — the parent then renders the full
// FoSummaryCard block. Drives off store state, so both call sites just drop it
// in above their `selectedFirmOffer` block.
export function FoDetailCard() {
  const pendingFoSummary = useSalesOrderStore((s) => s.pendingFoSummary);
  const selectedFirmOffer = useSalesOrderStore((s) => s.selectedFirmOffer);
  const isLoadingFirmOffer = useSalesOrderStore((s) => s.isLoadingFirmOffer);
  const eligibilityBanner = useSalesOrderStore((s) => s.eligibilityBanner);
  const selectFirmOffer = useSalesOrderStore((s) => s.selectFirmOffer);

  // Detail loaded (or nothing pending) — parent owns the render.
  if (selectedFirmOffer || !pendingFoSummary) return null;

  const fo = pendingFoSummary;
  const failed = !isLoadingFirmOffer && !!eligibilityBanner;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-baseline justify-between gap-3 text-base font-semibold">
          <span className="truncate font-mono">{fo.tranId}</span>
          <span className="shrink-0 text-xs font-normal tabular-nums text-success">
            {fo.balanceKg.toFixed(2)} / {fo.totalAuthorizedKg.toFixed(2)} kg
          </span>
        </CardTitle>
        <div className="truncate text-xs text-muted-foreground">
          {fo.customerName}
          {fo.projectName ? ` · ${fo.projectName}` : ''}
        </div>
      </CardHeader>
      <CardContent>
        {failed ? (
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-muted-foreground">Couldn’t load this Firm Offer.</span>
            <Button variant="outline" size="sm" onClick={() => selectFirmOffer(fo)}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </div>
        ) : (
          // PanelSkeleton brings its own border/padding; strip it inside the Card.
          <PanelSkeleton rows={6} className="border-0 p-0" />
        )}
      </CardContent>
    </Card>
  );
}
